/**
 * Screen 06 · Prep. The prototype's `<!-- 06 PREP -->` markup, against real
 * `PREP#` and `PREPDONE#` rows.
 *
 * The tab strip and the tick box are the whole interaction. Both are sized
 * for someone standing behind a bar with wet hands: 48 px buttons, and a
 * 56 px target around a 26 px box. Ticking is optimistic — the row goes
 * through immediately and rolls back if the write fails, because the
 * alternative is a bartender pressing a box twice.
 */

import { venueZone } from "../format.js";
import { hasFeature } from "../features.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isOverdue, serviceDate, type PrepRecurrence, clockOf } from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { NoVenue, useActingVenue } from "../NoVenue.js";
import type { ScreenProps } from "../screens.js";
import {
  EMPTY_TASK,
  prepApi,
  prepTabsFrom,
  RECURRENCES,
  type PrepDay,
  type PrepRow,
  type PrepTaskBody,
} from "./api.js";
import { Batches } from "./Batches.js";
import "./prep.css";

/** The address of the batches section, where a tab's name would go. */
const BATCHES = "batches";

/** Who may write the task list: a venue manager, or an org admin anywhere. */
function canManagePrep(
  me: { org: { role: string | null }; venues: readonly { venueId: string; role: string | null }[] },
  venueId: string | null,
): boolean {
  if (me.org.role === "owner" || me.org.role === "admin") return true;
  return me.venues.some((v) => v.venueId === venueId && v.role === "manager");
}

/** `2026-09-19T05:40:00Z` → `Fri 19 Sep · 15:40`, in the reader's own zone. */
function dueLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";
  const day = at.toLocaleDateString("en-AU", { timeZone: venueZone(), weekday: "short", day: "2-digit", month: "short" });
  const time = at.toLocaleTimeString("en-AU", { timeZone: venueZone(), hour: "2-digit", minute: "2-digit", hour12: false });
  return `${day} · ${time}`;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO instant → the `datetime-local` value for the same moment. */
function toLocalInput(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

function fromLocalInput(value: string): string {
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? "" : at.toISOString();
}

const dash = (v: string) => (v.trim() === "" ? "—" : v);

export function PrepScreen({ params }: ScreenProps) {
  const app = useApp();
  const { token, library, me, go } = app;
  const venueId = useActingVenue();

  const tabs = useMemo(() => prepTabsFrom(me), [me]);
  // Tonight on the venue's clock. The route defaults to the same zone, so an
  // unticked closing task and the tick that closed it land on one date.
  // The venue's clock, so "today" turns over at its own cut-off.
  const clock = clockOf(app.venueState?.venue);
  const [date, setDate] = useState(() => serviceDate(new Date(), { timeZone: clock }));
  // The dashboard's tonight-card opens a particular tab; so does a link.
  const [tab, setTab] = useState(() =>
    params.tab && tabs.includes(params.tab) ? params.tab : (tabs[0] ?? "Opening"),
  );
  // Batches (#161) sit beside the task tabs: what is made ahead, and making it.
  const [showBatches, setShowBatches] = useState(params.tab === BATCHES);
  // And the open tab goes back into the address, the first one as bare /prep.
  useEffect(() => {
    go({ kind: "prep", params: showBatches ? { tab: BATCHES } : tab === tabs[0] ? {} : { tab } }, { replace: true });
  }, [tab, tabs, go, showBatches]);
  const [day, setDay] = useState<PrepDay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());

  /** `null` when the form is closed, a task id when editing, "" when adding. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PrepTaskBody>(EMPTY_TASK);
  const [draftFromTab, setDraftFromTab] = useState("");
  const [saving, setSaving] = useState(false);

  const manage = canManagePrep(me, venueId);
  const specNames = useMemo(
    () => new Map(library.specs.map((s) => [s.specId, s.name])),
    [library.specs],
  );

  const load = useCallback(async () => {
    if (!token || !venueId) return;
    setLoading(true);
    try {
      setDay(await prepApi.day(token, venueId, date));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load tonight's prep");
    } finally {
      setLoading(false);
    }
  }, [token, venueId, date]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = day?.tabs ?? [];
  const active = groups.find((g) => g.tab === tab) ?? groups[0];

  /**
   * Tick a task on or off. The row flips first; a failed write puts it back
   * and says why, rather than leaving the list quietly wrong.
   */
  const toggle = useCallback(
    async (row: PrepRow) => {
      if (!token || !venueId || !day) return;
      const next = !row.done;
      setBusy((b) => new Set(b).add(row.taskId));
      setDay(patchRow(day, row.taskId, next));
      try {
        await prepApi.setDone(token, venueId, row.tab, row.taskId, day.serviceDate, next);
        setError(null);
      } catch (err) {
        setDay((d) => (d ? patchRow(d, row.taskId, row.done) : d));
        setError(
          err instanceof ApiError && err.status === 403
            ? "Your role does not allow ticking prep off at this venue."
            : err instanceof Error
              ? err.message
              : "That did not save",
        );
      } finally {
        setBusy((b) => {
          const rest = new Set(b);
          rest.delete(row.taskId);
          return rest;
        });
      }
    },
    [token, venueId, day],
  );

  const openNew = () => {
    setEditingId("");
    setDraftFromTab("");
    setDraft({
      ...EMPTY_TASK,
      tab: active?.tab ?? tabs[0] ?? "Opening",
      dueAt: new Date().toISOString(),
      sortOrder: (active?.tasks.length ?? 0) * 10,
    });
  };

  const openEdit = (row: PrepRow) => {
    setEditingId(row.taskId);
    setDraftFromTab(row.tab);
    setDraft({
      tab: row.tab,
      title: row.title,
      note: row.note,
      produces: row.produces,
      yield: row.yield,
      leadTime: row.leadTime,
      dueAt: row.dueAt,
      recurrence: row.recurrence,
      sortOrder: row.sortOrder,
    });
  };

  const submit = async () => {
    if (!token || !venueId || editingId === null) return;
    setSaving(true);
    try {
      if (editingId === "") await prepApi.create(token, venueId, draft);
      else await prepApi.save(token, venueId, draftFromTab, editingId, draft);
      setEditingId(null);
      setTab(draft.tab);
      await load();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the task");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Delete the task the form was opened on — addressed by the tab it was
   * loaded from, not the one the unsaved draft now names, and not by looking
   * the row up again in a list the user may have tabbed away from.
   */
  const remove = async () => {
    if (!token || !venueId || !editingId) return;
    setSaving(true);
    try {
      await prepApi.remove(token, venueId, draftFromTab, editingId);
      setEditingId(null);
      await load();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the task");
    } finally {
      setSaving(false);
    }
  };

  // Not an error — an organisation with no venue yet is a normal state, and
  // the fix depends on who is reading. `NoVenue` says which.
  if (!venueId) return <NoVenue what="a prep list" />;

  const outstanding = groups.reduce((n, g) => n + g.tasks.length - g.doneCount, 0);
  const total = groups.reduce((n, g) => n + g.tasks.length, 0);

  return (
    <div className="screen">
      <div className="split">
        <div className="pane">
          <div
            className="row screen-head"
            style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14, flexWrap: "wrap" }}
          >
            <div>
              <div className="label">
                Service {day?.serviceDate ?? date}
              </div>
              <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
                {loading
                  ? "Reading tonight's list…"
                  : total === 0
                    ? "No prep on file for this venue."
                    : `${outstanding} of ${total} still to do. Lead times shown.`}
              </div>
            </div>
            <span className="spacer" />
            <label className="field">
              <span>Service date</span>
              <input
                type="date"
                value={date}
                onChange={(e) =>
                  setDate(e.target.value || serviceDate(new Date(), { timeZone: clock }))
                }
              />
            </label>
            <div className="prep-tabs">
              {groups.map((g) => (
                <button
                  key={g.tab}
                  className="btn"
                  aria-pressed={!showBatches && g.tab === active?.tab}
                  onClick={() => {
                    setShowBatches(false);
                    setTab(g.tab);
                  }}
                >
                  {g.tab}
                  <span className="n">
                    {g.doneCount}/{g.tasks.length}
                  </span>
                </button>
              ))}
              <button className="btn" aria-pressed={showBatches} onClick={() => setShowBatches(true)}>
                Batches
              </button>
            </div>
          </div>

          {error && (
            <div style={{ padding: "0 20px 10px" }}>
              <div className="msg err">{error}</div>
            </div>
          )}

          <div className="scroll" style={{ padding: "0 20px 20px" }}>
            {showBatches && <Batches venueId={venueId} open={params.batch} />}
            <div className="table prep-table" hidden={showBatches}>
              <div className="thead">
                <span className="c-tick" />
                <span className="c-task">Task</span>
                <span className="c-produces">Produces</span>
                <span className="c-yield">Yield</span>
                <span className="c-lead">Lead</span>
                <span className="c-due">Start by</span>
                <span className="c-actions" />
              </div>

              {(active?.tasks ?? []).map((row) => {
                const specName = specNames.get(row.produces);
                const late = !row.done && isOverdue(row.dueAt);
                return (
                  <div className="prep-row" key={row.taskId}>
                    <span className="c-tick">
                      <button
                        className="prep-tick"
                        aria-pressed={row.done}
                        aria-label={`${row.done ? "Undo" : "Mark done"}: ${row.title}`}
                        disabled={busy.has(row.taskId)}
                        onClick={() => void toggle(row)}
                      >
                        <span className="box">{row.done ? "✓" : ""}</span>
                      </button>
                    </span>
                    <span className="c-task">
                      <span className={row.done ? "t done" : "t"}>{row.title}</span>
                      <span className="note">
                        {dash(row.note)}
                        {row.recurrence ? ` · ${row.recurrence}` : ""}
                      </span>
                    </span>
                    <span className="c-produces">
                      {specName ? (
                        <button
                          className="prep-link"
                          onClick={() => go({ kind: "recipes", params: { specId: row.produces } })}
                        >
                          {specName} →
                        </button>
                      ) : (
                        dash(row.produces)
                      )}
                    </span>
                    <span className="c-yield">{dash(row.yield)}</span>
                    <span className="c-lead">{dash(row.leadTime)}</span>
                    <span className={late ? "c-due late" : "c-due"}>{dueLabel(row.dueAt)}</span>
                    <span className="c-actions">
                      {manage && (
                        <button className="btn sm" onClick={() => openEdit(row)}>
                          Edit
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}

              {!loading && (active?.tasks.length ?? 0) === 0 && (
                <div className="pad label">Nothing on this tab.</div>
              )}
            </div>
          </div>

          <div className="prep-foot">
            <div className="label" style={{ fontSize: 11, letterSpacing: "0.08em" }}>
              Ticking a task records who did it, against {day?.serviceDate ?? date}
            </div>
            <span className="spacer" />
            {/* The prototype's footer sends you straight from the prep list to
                the count, because in the bar they happen back to back. */}
            {hasFeature(app.me, "stocktake") && (
              <button className="btn" onClick={() => go({ kind: "count" })}>
                Open count
              </button>
            )}
            {manage && (
              <button className="btn primary" onClick={openNew}>
                + New task
              </button>
            )}
          </div>
        </div>

        {manage && editingId !== null && (
          <aside className="aside sheet">
            <div className="row" style={{ padding: "16px 20px 0" }}>
              <div className="label">{editingId === "" ? "New task" : "Edit task"}</div>
            </div>
            <form
              className="prep-form scroll"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <label className="field">
                <span>Title</span>
                <input
                  type="text"
                  required
                  autoFocus
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Note</span>
                <textarea
                  value={draft.note}
                  onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Tab</span>
                <select
                  value={draft.tab}
                  onChange={(e) => setDraft({ ...draft, tab: e.target.value })}
                >
                  {[...new Set([...tabs, draft.tab])].map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              {/* One value, two ways in: pick a drink from the library and the
                  row links to its spec, or type anything else — "Hand-cut
                  ice", "Compliance" — and it stays a label. */}
              <div className="field">
                <span>Produces</span>
                <label className="field">
                  <select
                    aria-label="Produces a drink in the library"
                    value={specNames.has(draft.produces) ? draft.produces : ""}
                    onChange={(e) => setDraft({ ...draft, produces: e.target.value })}
                  >
                    <option value="">A drink in the library…</option>
                    {library.specs.map((s) => (
                      <option key={s.specId} value={s.specId}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <input
                    type="text"
                    aria-label="Produces a free label"
                    placeholder="…or a label, like Compliance"
                    value={specNames.has(draft.produces) ? "" : draft.produces}
                    onChange={(e) => setDraft({ ...draft, produces: e.target.value })}
                  />
                </label>
              </div>
              <div className="grid2">
                <label className="field">
                  <span>Yield</span>
                  <input
                    type="text"
                    placeholder="600 mL"
                    value={draft.yield}
                    onChange={(e) => setDraft({ ...draft, yield: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span>Lead time</span>
                  <input
                    type="text"
                    placeholder="12 hours"
                    value={draft.leadTime}
                    onChange={(e) => setDraft({ ...draft, leadTime: e.target.value })}
                  />
                </label>
              </div>
              <label className="field">
                <span>Start by</span>
                <input
                  type="datetime-local"
                  required
                  value={toLocalInput(draft.dueAt)}
                  onChange={(e) => setDraft({ ...draft, dueAt: fromLocalInput(e.target.value) })}
                />
              </label>
              <div className="grid2">
                <label className="field">
                  <span>Repeats</span>
                  <select
                    value={draft.recurrence ?? ""}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        recurrence: (e.target.value || null) as PrepRecurrence | null,
                      })
                    }
                  >
                    {RECURRENCES.map((r) => (
                      <option key={r ?? "once"} value={r ?? ""}>
                        {r ?? "Once"}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Sort order</span>
                  <input
                    type="number"
                    className="num-input"
                    min={0}
                    max={10000}
                    value={draft.sortOrder}
                    onChange={(e) =>
                      setDraft({ ...draft, sortOrder: Number(e.target.value) || 0 })
                    }
                  />
                </label>
              </div>

              <div className="row" style={{ gap: 10 }}>
                <button className="btn primary" type="submit" disabled={saving}>
                  {saving ? "Saving…" : editingId === "" ? "Add task" : "Save task"}
                </button>
                <button className="btn" type="button" onClick={() => setEditingId(null)}>
                  Cancel
                </button>
                <span className="spacer" />
                {editingId !== "" && (
                  <button
                    className="btn danger"
                    type="button"
                    disabled={saving}
                    onClick={() => void remove()}
                  >
                    Delete
                  </button>
                )}
              </div>
            </form>
          </aside>
        )}
      </div>
    </div>
  );
}

/** One row's done state, without re-reading the day. */
function patchRow(day: PrepDay, taskId: string, done: boolean): PrepDay {
  return {
    ...day,
    tabs: day.tabs.map((g) => {
      if (!g.tasks.some((t) => t.taskId === taskId)) return g;
      const tasks = g.tasks.map((t) => (t.taskId === taskId ? { ...t, done } : t));
      return { ...g, tasks, doneCount: tasks.filter((t) => t.done).length };
    }),
  };
}
